import type { PlanInterval } from '@/payment/types';

export const CHECKOUT_RETURN_OFFER_CAMPAIGN =
  'checkout_return_subscription_15_v1';
export const CHECKOUT_RETURN_OFFER_DISCOUNT_PERCENT = 15;
export const CHECKOUT_RETURN_OFFER_PROMOTION_CODE = 'BBH5ZXWG';
export const CHECKOUT_RETURN_OFFER_TTL_MS = 30 * 60 * 1000;
export const CHECKOUT_RETURN_OFFER_STORAGE_KEY =
  'grok-imagine:checkout-return-offer';
export const CHECKOUT_RETURN_STATUS_PARAM = 'checkout';
export const CHECKOUT_RETURN_CANCELLED_VALUE = 'cancelled';

export type IssuedCheckoutReturnOffer = {
  token: string;
  planId: string;
  planName: string;
  priceId: string;
  interval: PlanInterval;
  originalAmount: number;
  currency: string;
  expiresAtMs: number;
  returnTo: string;
};

export type CheckoutReturnOfferMarker = Omit<
  IssuedCheckoutReturnOffer,
  'expiresAtMs'
> & {
  expiresAt: string;
};

export function isCheckoutReturnOfferPriceEligible(
  paymentType: string,
  interval: string | undefined,
  currency: string
) {
  return (
    paymentType === 'subscription' &&
    (interval === 'month' || interval === 'year') &&
    currency.trim().toUpperCase() === 'USD'
  );
}

export function getCheckoutReturnOfferDiscountedAmount(amount: number) {
  return Math.round(
    (amount * (100 - CHECKOUT_RETURN_OFFER_DISCOUNT_PERCENT)) / 100
  );
}

export function appendCheckoutReturnCancelled(url: string) {
  const parsed = new URL(url);
  parsed.searchParams.set(
    CHECKOUT_RETURN_STATUS_PARAM,
    CHECKOUT_RETURN_CANCELLED_VALUE
  );
  return parsed.toString();
}

export function hasSuccessfulCheckoutReturn(search: string) {
  const params = new URLSearchParams(search);
  return Boolean(
    params.get('session_id') ||
      params.get('checkout_id') ||
      params.get('credits_session_id')
  );
}

export function createCheckoutReturnOfferMarker(
  offer: IssuedCheckoutReturnOffer,
  nowMs = Date.now()
): CheckoutReturnOfferMarker | null {
  if (
    !offer.token.trim() ||
    !offer.planId.trim() ||
    !offer.planName.trim() ||
    !offer.priceId.trim() ||
    (offer.interval !== 'month' && offer.interval !== 'year') ||
    !offer.returnTo.startsWith('/') ||
    offer.returnTo.startsWith('//') ||
    !Number.isInteger(offer.originalAmount) ||
    offer.originalAmount <= 0 ||
    offer.currency.trim().toUpperCase() !== 'USD' ||
    !Number.isFinite(offer.expiresAtMs) ||
    offer.expiresAtMs <= nowMs
  ) {
    return null;
  }

  return {
    token: offer.token,
    planId: offer.planId,
    planName: offer.planName,
    priceId: offer.priceId,
    interval: offer.interval,
    originalAmount: offer.originalAmount,
    currency: offer.currency.toUpperCase(),
    expiresAt: new Date(offer.expiresAtMs).toISOString(),
    returnTo: offer.returnTo,
  };
}

export function writeCheckoutReturnOfferMarker(
  marker: CheckoutReturnOfferMarker
) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      CHECKOUT_RETURN_OFFER_STORAGE_KEY,
      JSON.stringify(marker)
    );
  } catch {}
}

export function readCheckoutReturnOfferMarker(
  nowMs = Date.now()
): CheckoutReturnOfferMarker | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(
      CHECKOUT_RETURN_OFFER_STORAGE_KEY
    );
    if (!raw) return null;
    const marker = JSON.parse(raw) as Partial<CheckoutReturnOfferMarker>;
    const expiresAtMs =
      typeof marker.expiresAt === 'string'
        ? Date.parse(marker.expiresAt)
        : Number.NaN;
    if (
      typeof marker.token !== 'string' ||
      !marker.token.trim() ||
      typeof marker.planId !== 'string' ||
      !marker.planId.trim() ||
      typeof marker.planName !== 'string' ||
      !marker.planName.trim() ||
      typeof marker.priceId !== 'string' ||
      !marker.priceId.trim() ||
      (marker.interval !== 'month' && marker.interval !== 'year') ||
      typeof marker.originalAmount !== 'number' ||
      !Number.isInteger(marker.originalAmount) ||
      marker.originalAmount <= 0 ||
      typeof marker.currency !== 'string' ||
      typeof marker.returnTo !== 'string' ||
      !marker.returnTo.startsWith('/') ||
      marker.returnTo.startsWith('//') ||
      marker.currency.trim().toUpperCase() !== 'USD' ||
      !Number.isFinite(expiresAtMs) ||
      nowMs >= expiresAtMs
    ) {
      clearCheckoutReturnOfferMarker();
      return null;
    }
    return marker as CheckoutReturnOfferMarker;
  } catch {
    clearCheckoutReturnOfferMarker();
    return null;
  }
}

export function clearCheckoutReturnOfferMarker() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(CHECKOUT_RETURN_OFFER_STORAGE_KEY);
  } catch {}
}

export function isOriginalSubscriptionCheckoutEligibleForReturnOffer({
  status,
  paymentStatus,
  mode,
  metadata,
  userId,
  planId,
  priceId,
}: {
  status: string | null;
  paymentStatus: string;
  mode: string | null;
  metadata: Record<string, string>;
  userId: string;
  planId: string;
  priceId: string;
}) {
  return (
    status === 'open' &&
    paymentStatus === 'unpaid' &&
    mode === 'subscription' &&
    metadata.userId === userId &&
    metadata.planId === planId &&
    metadata.priceId === priceId &&
    metadata.checkoutReturnOfferEligible === 'true' &&
    !metadata.checkoutReturnOfferCampaign
  );
}
